import React, { useState, useEffect, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";

import { getAccessSnapshot } from "../../context/AccessControl/accessSnapshot";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import UserGroupModal from "../../components/UserGroupModal";
import toastError from "../../errors/toastError";

import { listUserGroups, deleteUserGroup } from "../../services/access.api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: -8,
    marginBottom: 8
  },
  count: {
    display: "inline-block",
    minWidth: 24,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 12,
    background: theme.palette.action.selected
  },
  muted: { color: theme.palette.text.disabled, fontSize: 13 }
}));

/**
 * Subagrupaciones (equipos).
 *
 * La columna "Departamentos" no es decorativa: son las colas que atiende el
 * equipo, y es lo que permite a su supervisor ver los tickets `pending` sin
 * asignar —los que no tienen dueño del que heredar pertenencia— (doc 0380 §5).
 * Un equipo sin departamentos deja a su supervisor sin cola de entrada.
 */
const UserGroups = () => {
  const classes = useStyles();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listUserGroups();
      setGroups(data ?? []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleDelete = async () => {
    try {
      await deleteUserGroup(deleting.id);
      toast.success("Subagrupación eliminada");
      fetchGroups();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
      setConfirmOpen(false);
    }
  };

  /**
   * Con el modo equipos apagado esta página no existe para la empresa: ocultarla
   * del menú no basta, porque la URL sigue siendo tecleable.
   *
   * Solo redirige si el snapshot ESTÁ cargado. Si falló al cargarse no se
   * bloquea a nadie —misma regla que `<Can>`: un fallo al leer permisos nunca
   * puede cerrar una puerta que estaba abierta—.
   */
  const acceso = getAccessSnapshot();
  if (acceso.loaded && !acceso.teamsEnabled) return <Redirect to="/tickets" />;

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `¿Eliminar la subagrupación ${deleting.name}?` : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Sus miembros dejarán de compartir visibilidad, pero no se elimina ningún
        usuario ni ningún ticket.
      </ConfirmationModal>

      <UserGroupModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        groupId={selectedId}
        onSaved={fetchGroups}
      />

      <MainHeader>
        <Title>Subagrupaciones</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedId(null);
              setModalOpen(true);
            }}
          >
            Nueva subagrupación
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.subtitle}>
          Agrupá usuarios en equipos y asigná qué departamentos atienden. El
          supervisor de un equipo ve la actividad de sus miembros.
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Supervisor</TableCell>
              <TableCell align="center">Miembros</TableCell>
              <TableCell align="center">Departamentos</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={5} />
            ) : groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" className={classes.muted}>
                  Sin subagrupaciones. La supervisión es opcional: si no creás
                  ninguna, el administrador sigue viendo todos los tickets.
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>
                    {group.supervisorName || (
                      <span className={classes.muted}>Sin supervisor</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <span className={classes.count}>{group.membersCount}</span>
                  </TableCell>
                  <TableCell align="center">
                    <span className={classes.count}>{group.queuesCount}</span>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedId(group.id);
                        setModalOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleting(group);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default UserGroups;
